import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmationDialogComponent, LoadingOverlayComponent, SnackbarService } from '@shared/ui';
import { Subject } from 'rxjs';
import { filter, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { CaseDataManagerComponent } from '../case-data-manager/case-data-manager.component';
import { MethodTypeManagerComponent } from '../../method-types/method-type-manager/method-type-manager.component';

import { CaseTypeEditorFacade } from './case-type-editor.facade';

/**
 * Smart case-type editor: the name + description fields and the two managed
 * sections (case data fields, method types). A pure draft producer — save/cancel
 * emit the shared state's one-shot outcome; persistence belongs to the consumer
 * (the host page runs the protocol cascade on saved). Mirrors
 * `MethodTypeEditorComponent`.
 *
 * Deliberately declares NO providers: the host page owns the full DI scope
 * (editor tier, manager stacks, shared staging ledgers, cascade tier), which
 * also keeps the scope overridable by stories and specs.
 */
@Component({
  selector: 'ts-case-type-editor',
  templateUrl: './case-type-editor.component.html',
  styleUrls: ['./case-type-editor.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatDialogModule,
    LoadingOverlayComponent,
    CaseDataManagerComponent,
    MethodTypeManagerComponent,
  ],
})
export class CaseTypeEditorComponent implements OnInit, OnDestroy {
  public readonly loading$ = this.facade.loading$;
  public readonly saving$ = this.facade.saving$;
  public readonly error$ = this.facade.error$;
  public readonly nameError$ = this.facade.nameError$;

  public readonly nameControl = new FormControl<string>('', { nonNullable: true });
  public readonly descriptionControl = new FormControl<string>('', { nonNullable: true });

  private readonly destroy$ = new Subject<void>();

  constructor(
    public readonly facade: CaseTypeEditorFacade,
    private readonly dialog: MatDialog,
    private readonly snackbar: SnackbarService
  ) {}

  ngOnInit(): void {
    // state → form (seeded once the load resolves; in create mode loading never
    // flips to true so this fires immediately with the empty defaults).
    this.facade.loading$
      .pipe(
        filter((loading) => !loading),
        take(1),
        withLatestFrom(this.facade.name$, this.facade.description$),
        takeUntil(this.destroy$)
      )
      .subscribe(([, name, description]) => {
        this.nameControl.setValue(name, { emitEvent: false });
        this.descriptionControl.setValue(description, { emitEvent: false });
      });

    // form → state
    this.nameControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => this.facade.setName(value));
    this.descriptionControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.facade.setDescription(value));

    // Cascade snackbar dispatchers (pipeline/method-type editor parity): long
    // duration because the message references a specific label and the user
    // needs time to read it before deciding to retry.
    this.facade.labelSaveFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ name }) =>
        this.snackbar.show('pipeline_editor.snackbar.label_save_failed', { duration: 0, params: { name } })
      );
    this.facade.labelConfigSaveFailed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ name }) =>
        this.snackbar.show('pipeline_editor.snackbar.label_config_save_failed', { duration: 0, params: { name } })
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSave(): void {
    this.facade.save();
  }

  public onCancel(): void {
    this.facade.hasChanges$.pipe(take(1), takeUntil(this.destroy$)).subscribe((hasChanges) => {
      if (!hasChanges) {
        this.facade.cancel();
        return;
      }
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            title: 'case_type_editor.warnings.cancel_unsaved.title',
            text: 'case_type_editor.warnings.cancel_unsaved.text',
            acceptButtonText: 'button.discard_changes',
            cancelButtonText: 'pipeline_step_editor.button.keep_editing',
            warningDialog: true,
            defaultAction: 'cancel',
          },
          hasBackdrop: true,
        })
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((result) => {
          if (result === 'accept') this.facade.cancel();
        });
    });
  }
}
